/*
  "Available koi like this one" on sold koi listings.

  Companion to snippets/koi-similar-available.liquid. When the selected
  variant is sold, fetch sections/ck-similar-koi.liquid through the Section
  Rendering API on the koi collection with the Variety facet applied, rank
  the returned per-koi cards by closeness to the sold fish, and drop the
  best few into the grid.

  Same variety only. A shopper on a sold Asagi is not helped by a Hi Utsuri
  from the same breeder, so there is no breeder or "anything available"
  fallback: if the store has no other koi of that variety online the module
  stays hidden and the "Request one like this" box below is the next step.

  Two fetches at most. The first carries a price band (0.4x to 2.5x the sold
  price) on the price facet. The section only sees the first 50 listings of
  a newest-first collection, and after a tosai drop those are all 20cm
  fish, so without the band a shopper on a 45cm Showa would be offered four
  $135 tosai while a dozen 44-58cm Showa sit further down. When the band
  leaves the grid short (a $135 Asagi tosai whose only same-price company
  is one other tosai) the second fetch drops the band: the 50-63cm Asagi
  at $1,350-$6,650 are a better answer than a Hi Utsuri at $135.

  Facets this store has enabled (there is no Vendor or Availability facet,
  the section checks availability itself): filter.v.m.custom.base_variety,
  filter.v.m.custom.breeder, filter.v.price.gte / .lte.

  Closeness, lower is better:
    tier * 100      which fetch found it (inside the price band 0, outside 1)
    |size diff| cm  a 45cm shopper should see 40-50cm fish before 20cm tosai
    |ln(price/ref)| * 20   a 2x price gap costs ~14 points, roughly 14cm
    breeder mismatch  +5, a tiebreaker only

  The pool skips the listing the shopper is already on: its other letters
  are the pills right above, and a card that links back to this page reads
  as a bug.

  Renders nothing until a fetch returns at least one card, so a sold page
  with no matches simply keeps the "Request one like this" box below.

  The 404 page reuses this element for dead listing URLs (ck-not-found.js).
  It passes a one-entry variants map built from the old handle (variety,
  breeder and size, no price), its own event prefix, and data-fallback: the
  filters to fetch when the old variety has no koi online (that breeder, or
  newest first), since a dead link has no "Request one like this" box below
  it. Each update fires ck-similar:update with the number of cards shown.
*/
(() => {
  if (customElements.get('ck-similar-koi')) return;

  const SECTION_ID = 'ck-similar-koi';

  const track = (name, props) => {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    window.posthog.capture(name, props || {});
  };

  class CkSimilarKoi extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';
      this.grid = this.querySelector('[data-ck-similar-grid]');
      this.allLink = this.querySelector('[data-ck-similar-all]');
      this.varietyEl = this.querySelector('[data-ck-similar-variety]');
      this.countEl = this.querySelector('[data-ck-similar-count]');
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.collectionUrl = this.dataset.collectionUrl || '/collections/all';
      this.productId = String(this.dataset.productId || '');
      this.eventPrefix = this.dataset.eventPrefix || 'sold_koi_similar';
      try {
        this.variants = JSON.parse(this.dataset.variants || '{}');
      } catch (e) {
        this.variants = {};
      }
      this.cache = new Map();
      this.token = 0;
      this.shownFor = null;

      this.addEventListener('click', (e) => this.onClick(e));
      this.bindPubSub();

      if (this.dataset.initiallySold === 'true') this.update(this.dataset.initialVariantId);
    }

    /* Dawn's pub/sub globals load with defer; poll briefly until they exist. */
    bindPubSub() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (evt) => this.onVariant(evt));
      } else {
        setTimeout(() => this.bindPubSub(), 200);
      }
    }

    onVariant(evt) {
      const data = evt && evt.data;
      if (!data || String(data.sectionId) !== String(this.dataset.sectionId)) return;
      const variant = data.variant;
      if (!variant || variant.available === false) {
        this.update(variant ? variant.id : null);
      } else {
        this.hide();
      }
    }

    hide() {
      this.token += 1;
      this.classList.remove('is-visible');
    }

    async update(variantId) {
      const ref = this.variants[String(variantId)] || null;
      const token = (this.token += 1);
      const pool = await this.candidates(ref);
      if (token !== this.token) return;

      /* The 404 asks for one card per listing: four letters of one group
         read as one fish four times to someone who just lost theirs. */
      const seen = new Set();
      const ranked = pool
        .map((item) => ({ ...item, score: this.score(item, ref) }))
        .sort((a, b) => a.score - b.score)
        .filter((item) => {
          if (this.dataset.onePerListing !== 'true') return true;
          if (seen.has(item.productId)) return false;
          seen.add(item.productId);
          return true;
        })
        .slice(0, this.limit);

      if (!ranked.length) {
        this.classList.remove('is-visible');
        this.announce(0, pool);
        return;
      }

      this.grid.replaceChildren(...ranked.map((item) => item.el));
      this.setFooter(ref, ranked, pool.length);
      this.classList.add('is-visible');
      this.announce(ranked.length, pool);

      const key = String(variantId) + ':' + ranked.map((r) => r.key).join(',');
      if (this.shownFor !== key) {
        this.shownFor = key;
        track(this.eventPrefix + '_shown', {
          product_id: this.productId,
          variant_id: variantId ? String(variantId) : null,
          variety: ref ? ref.variety : null,
          count: ranked.length,
          pool: pool.length,
          best_tier: ranked[0].tier,
        });
      }
    }

    announce(count, pool) {
      this.dispatchEvent(
        new CustomEvent('ck-similar:update', {
          bubbles: true,
          detail: { count, pool: pool.length, tier: count ? Math.min(...pool.map((p) => p.tier)) : null },
        })
      );
    }

    /*
      Widen the net only as far as needed. Each fetch is a full collection
      page render, so stop as soon as the pool can fill the grid. Both
      fetches stay inside the sold fish's variety; a listing with no
      variety on the variant has nothing to match and gets no cards.
    */
    async candidates(ref) {
      const pool = new Map();
      const add = (items, tier) => {
        items.forEach((item) => {
          if (!pool.has(item.key)) pool.set(item.key, { ...item, tier });
        });
      };
      if (!ref || !ref.facet) {
        if (this.dataset.fallback) {
          try {
            add(await this.fetchPool(JSON.parse(this.dataset.fallback)), 2);
          } catch (e) {
            /* malformed fallback: no cards */
          }
        }
        return [...pool.values()];
      }

      const variety = { 'filter.v.m.custom.base_variety': ref.facet };
      if (ref.price > 0) {
        /* Price facet values are in major units; variant prices are cents. */
        add(
          await this.fetchPool({
            ...variety,
            'filter.v.price.gte': String(Math.floor((ref.price * 0.4) / 100)),
            'filter.v.price.lte': String(Math.ceil((ref.price * 2.5) / 100)),
          }),
          0
        );
      }
      if (pool.size < this.limit) {
        add(await this.fetchPool(variety), 1);
      }
      return [...pool.values()];
    }

    async fetchPool(filters) {
      const params = new URLSearchParams(filters);
      params.set('section_id', SECTION_ID);
      const url = this.collectionUrl + '?' + params.toString();

      if (!this.cache.has(url)) {
        this.cache.set(
          url,
          fetch(url, { headers: { Accept: 'text/html' } })
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        );
      }
      const html = await this.cache.get(url);
      if (!html) return [];

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = [];
      doc.querySelectorAll('li[data-ck-koi]').forEach((li) => {
        if (String(li.dataset.productId) === this.productId) return;
        if (li.querySelector('.koi-card--sold')) return;
        items.push({
          key: String(li.dataset.variantId),
          el: li,
          productId: String(li.dataset.productId),
          size: parseFloat(li.dataset.sizeCm) || 0,
          price: parseInt(li.dataset.price, 10) || 0,
          breeder: (li.dataset.breeder || '').trim().toLowerCase(),
          facet: (li.dataset.facetVariety || '').trim().toLowerCase(),
        });
      });
      return items;
    }

    score(item, ref) {
      let score = item.tier * 100;
      if (!ref) return score;
      if (ref.size > 0 && item.size > 0) score += Math.abs(item.size - ref.size);
      if (ref.price > 0 && item.price > 0) score += Math.abs(Math.log(item.price / ref.price)) * 20;
      if (ref.breeder && item.breeder !== String(ref.breeder).trim().toLowerCase()) score += 5;
      return score;
    }

    setFooter(ref, ranked, poolSize) {
      /* Every card is the sold fish's variety, so the link always is too. It
         opens the facet, so name the facet: a sold Tancho Showa's "see all"
         lands on every Showa, and the label should say so. */
      if (!ref || !ref.facet) return;
      if (this.varietyEl) this.varietyEl.textContent = ref.facet;
      const params = new URLSearchParams({ 'filter.v.m.custom.base_variety': ref.facet });
      if (this.allLink) this.allLink.href = this.collectionUrl + '?' + params.toString() + '#ProductGridContainer';
      if (this.countEl) {
        const more = poolSize - ranked.length;
        this.countEl.hidden = more <= 0;
        this.countEl.textContent = more > 0 ? '+' + more + ' more' : '';
      }
    }

    onClick(e) {
      const all = e.target.closest('[data-ck-similar-all]');
      if (all) {
        track(this.eventPrefix + '_see_all_click', { product_id: this.productId, href: all.getAttribute('href') });
        return;
      }
      const li = e.target.closest('li[data-ck-koi]');
      if (!li || !e.target.closest('a')) return;
      const items = [...this.grid.querySelectorAll('li[data-ck-koi]')];
      track(this.eventPrefix + '_click', {
        product_id: this.productId,
        target_product_id: li.dataset.productId,
        target_variant_id: li.dataset.variantId,
        target_variety: li.dataset.variety,
        position: items.indexOf(li) + 1,
      });
    }
  }

  customElements.define('ck-similar-koi', CkSimilarKoi);
})();
